import type {
  Favorite,
  FavoriteCreateInput,
  FavoriteList,
  FavoriteListCreateInput,
} from '~/models/Favorites'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import stringUtils from '~/utils/stringUtils'
import { colors, getColorForId } from '~/utils/colors'
import useArrayUtils from '~/utils/arrayUtils'
import useAuthStore from '~/stores/auth'
import { useFavoritesApi } from '~/api/favoritesApi'

const QUERY_KEY: Readonly<string> = 'favorite-lists'

const FAVORITES_LIMIT: Readonly<number> = 30
const FAVORITES_NAME_LENGTH: Readonly<number> = 80

export const useFavorites = () => {
  const queryClient = useQueryClient()
  const { truncate } = stringUtils()
  const {
    getFavoriteLists: apiGetFavoriteLists,
    updateFavoriteList: apiUpdateFavoriteList,
    deleteFavoriteList: apiDeleteFavoriteList,
    createFavoriteList: apiCreateFavoriteList,
    removeFavoriteFromList: apiRemoveFavoriteFromList,
    createFavorite: apiCreateFavorite,
    updateFavorite: apiUpdateFavorite
  } = useFavoritesApi()
  const {
    isAuthenticated,
    isTeacher,
    userId,
  } = storeToRefs(useAuthStore())
  const { truthy } = useArrayUtils()

  const addItemToList = useMutation({
    mutationFn: ({ item, list }: { item: FavoriteCreateInput, list: FavoriteList }) => apiCreateFavorite(item, list),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      const cachedList = queryClient.getQueryData<FavoriteList>(['favorite-list', variables.list.id])
      if (cachedList) {
        const updatedFavorites = [...(cachedList.favorites ?? []), data]
        queryClient.setQueryData(['favorite-list', variables.list.id], {
          ...cachedList,
          positions: updatedFavorites.map(({ id }) => id),
          favorites: updatedFavorites,
        })
      }
    }
  })

  const updateFavoriteItem = useMutation({
    mutationFn: ({ item, list }: { item: Favorite, list: FavoriteList }) => apiUpdateFavorite(item),
    onSuccess: (data, variables) => {
      const listKey = ['favorite-list', variables.list.id]
      const itemKey = ['favorite', variables.item.id]
      queryClient.setQueryData(itemKey, data)
      const cachedList = queryClient.getQueryData<FavoriteList>(listKey)
      if (cachedList) {
        queryClient.setQueryData(listKey, {
          ...cachedList,
          favorites: cachedList.favorites?.map(item => item.id === data.id ? { ...data } : { ...item }) ?? null,
        })
      }
    },
  })

  const deleteFavoriteList = useMutation({
    mutationFn: (list: FavoriteList) => apiDeleteFavoriteList(list),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['favorite-list', variables.id] })
      variables.favorites?.forEach(({ id }) => queryClient.invalidateQueries({
        queryKey: ['favorite', id]
      }))
      const cachedLists = queryClient.getQueryData<FavoriteList[]>([QUERY_KEY])
      if (cachedLists) {
        const updatedLists = cachedLists.filter(({ id }) => id !== variables.id)
        queryClient.setQueryData([QUERY_KEY], updatedLists)
      }
    },
  })

  const updateFavoriteList = useMutation({
    mutationFn: (item: { list: FavoriteList, signal?: AbortSignal }) => apiUpdateFavoriteList(item.list, item.signal),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['favorite-list', variables.list.id], variables.list)
      const cachedLists = queryClient.getQueryData<FavoriteList[]>([QUERY_KEY]) ?? []
      if (cachedLists) {
        const updatedLists = cachedLists.map(list => list.id === variables.list.id ? variables.list : list)
        queryClient.setQueryData([QUERY_KEY], updatedLists)
      }
    },
  })

  const createFavoriteList = useMutation({
    mutationFn: (newList: FavoriteListCreateInput) => apiCreateFavoriteList(newList),
    onSuccess: (data) => {
      queryClient.setQueryData(['favorite-list', data.id], data)
      const cachedLists = queryClient.getQueryData<FavoriteList[]>([QUERY_KEY])
      if (cachedLists) {
        const updatedLists = [...cachedLists, data]
        queryClient.setQueryData([QUERY_KEY], updatedLists)
      }
    },
  })

  const removeFavoriteFromList = useMutation({
    mutationFn: ({ item, list }: { item: Favorite, list: FavoriteList }) => apiRemoveFavoriteFromList({ item, list }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      const cachedList = queryClient.getQueryData<FavoriteList>(['favorite-list', variables.list.id])
      if (cachedList) {
        cachedList.favorites = (cachedList?.favorites ?? []).filter(({ id }) => id !== variables.item.id)
        queryClient.setQueryData(['favorite-list', variables.list.id], cachedList)
      }
    },
  })

  const duplicateFavoriteList = useMutation({
    mutationFn: async (list: FavoriteList) => {
      const input = {
        list: {
          name: list.name?.includes('(kopi)') ? list.name : `${list.name} (kopi)`,
          ownerUserId: userId.value ?? null,
          colorId: list.colorId,
          description: list.description,
          subjects: list.subjects,
          grades: list.grades,
          positions: [],
        },
        favorites: (list.favorites ?? []).map(favorite => ({
          name: favorite.name,
          aunivers: favorite.aunivers,
          file: favorite.file,
          link: favorite.link,
          hidden: favorite.hidden,
        }))
      }
      const createdList = await apiCreateFavoriteList(input.list)
      if (!input.favorites.length) {
        return createdList
      }
      const createdFavorites = await Promise.all(
        input.favorites.map(favorite => apiCreateFavorite({
          name: favorite.name,
          aunivers: favorite.aunivers,
          hidden: favorite.hidden,
          file: favorite.file,
          link: favorite.link,
        }, createdList))
      )
      const updatedList = await apiUpdateFavoriteList({
        ...createdList,
        positions: createdFavorites.map(({ id }) => id),
      })
      return {
        ...updatedList,
        favorites: createdFavorites,
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['favorite-list', data.id], data)
      const cachedLists = queryClient.getQueryData<Array<FavoriteList>>([QUERY_KEY])
      if (cachedLists) {
        queryClient.setQueryData([QUERY_KEY], [...cachedLists, data])
      }
    },
  })

  const favoriteLists = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => apiGetFavoriteLists(),
    staleTime: 1000 * 3600 * 2, // 2 hours
    enabled: computed(() => isAuthenticated.value),
    select: data => data.sort((a, b) =>
      new Date(b?.created || 0).getTime() - new Date(a?.created || 0).getTime()
    ),
  })

  const locationIds = computed(() =>
    favoriteLists
      .data
      .value
      ?.flatMap(({ favorites }) => favorites?.map(({ aunivers }) => aunivers?.locationId).filter(truthy<number>) ?? [])
      .map(id => Number(id))
      ?? []
  )

  const sortFavorites = (a: Favorite, b: Favorite, list?: FavoriteList) => {
    const positions = list?.positions ?? []
    const diff = positions.indexOf(a.id) - positions.indexOf(b.id)
    if (diff !== 0)
      return diff

    return new Date(a?.created || '').getTime() - new Date(b?.created || '').getTime()
  }

  const safeName = (name: string) => truncate(name
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, ''), FAVORITES_NAME_LENGTH)

  const getColor = (list: FavoriteList) => getColorForId(list.id ?? 0, colors)

  const isMine = (list: FavoriteList) => list.ownerUserId === userId.value

  const canEditList = (list: FavoriteList) => isTeacher.value || isMine(list)

  const canDeleteList = (list: FavoriteList) => list.deletable && isMine(list)

  return {
    updateFavoriteItem,
    createFavoriteList,
    deleteFavoriteList,
    removeFavoriteFromList,
    updateFavoriteList,
    favoriteLists,
    addItemToList,
    locationIds,
    getColor,
    sortFavorites,
    isMine,
    canEditList,
    canDeleteList,
    duplicateFavoriteList,
    FAVORITES_LIMIT,
    FAVORITES_NAME_LENGTH,
    safeName,
  }
}
